import React from 'react';
import PropTypes, {
  arrayOf, bool, shape, func, string, number
} from 'prop-types';
import { useDomPath } from '@thd-olt-functional/utils';
import { Fulfillment } from '@thd-olt-component-react/fulfillment';
import { BuyboxActions } from './BuyboxActions';
import { BuyboxMessage } from './BuyboxMessage';
import { track } from '../../track';
import { isMobile } from '../../util';

const BuyboxLiteQuantity = ({
  itemId,
  dualPath,
  isItemOOS,
  unavailable,
  storeId,
  isShowATC,
  covidAlert,
  storeZip,
  channel,
  zipCode,
  storeName,
  onATCClick,
  fulfillment,
  paypalEligible,
  isATCDisabled,
  fulfillmentMethod,
  onFulfillmentChange,
  bopisHolidayTiming,
  hideInstantCheckout,
  hidePaypalCheckout,
  directData,
  quantityLimit,
  quantityLimitExceed,
  minimumOrderQuantity,
  minimumBopisQuantity,
  maximumBopisQuantity,
  configuratorData,
  subscription,
  currentSelectedFulfillment,
  configurableProductDetails,
  availableQuantity,
  showQuantity,
  quantity,
  fallbackMode,
  customRisMessage,
  onQuantityChange,
  virtualAssistance,
  risMessageEligible,
  isOutOfStockOnline,
  isRisMessageEnabled,
  onDeliveryOptionsZipCodeChange,
}) => {
  const [pathForAdvisoryMessage] = useDomPath();
  const isMobileDevice = isMobile(channel);
  return (
    <div
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={`sui-grid sui-ml-[-10px] sui-mr-[-10px] 
        ${isMobileDevice ? 'sui-w-auto' : 'sui-w-full'}`}
      data-component="BuyboxLite"
    >
      <div className="sui-grid-cols-12">
        <div className={`${isMobileDevice ? 'sui-p-2' : 'sui-mb-3'} sui-bg-primary sui-overflow-hidden"`}>
          <div>
            {!unavailable && (
              <Fulfillment
                itemId={itemId}
                covidAlert={covidAlert}
                onChange={onFulfillmentChange}
                onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                bopisHolidayTiming={bopisHolidayTiming}
                directData={directData}
                isBuyBoxLite
                quantity={quantity}
                configurableProductDetails={configurableProductDetails}
                currentSelectedFulfillment={currentSelectedFulfillment}
              />
            )}
          </div>
          <div className="sui-mt-3">
            {
              !isOutOfStockOnline && isShowATC && !!minimumOrderQuantity && (
                <BuyboxMessage
                  onRender={() => {
                    if (pathForAdvisoryMessage.length) {
                      track('advisory-message', {
                        message: `This item can only be purchased in quantities of ${minimumOrderQuantity} or more`,
                        path: pathForAdvisoryMessage
                      });
                    }
                  }}
                  message={`This item can only be purchased in quantities of ${minimumOrderQuantity} or more`}
                  status="warning"
                />
              )
            }
            {quantityLimit > 0 && quantity > (quantityLimit - 1) && (
              <BuyboxMessage
                onRender={() => {
                  if (pathForAdvisoryMessage.length) {
                    track('advisory-message', {
                      message: `This item has a limit of ${quantityLimit} per order.`,
                      path: pathForAdvisoryMessage
                    });
                  }
                }}
                message={`This item has a limit of ${quantityLimit} per order.`}
                status="warning"
              />
            )}
            {
              !!(minimumBopisQuantity) && (
                <BuyboxMessage
                  message={
                    `This item has a minimum quantity of
                              ${minimumBopisQuantity} for Pickup at ${storeName}.`
                  }
                  status="info"
                />
              )
            }
            {
              !!(maximumBopisQuantity) && (
                <BuyboxMessage
                  message={
                    `This item has a maximum quantity of
                              ${maximumBopisQuantity} for Pickup at ${storeName}.`
                  }
                  status="info"
                />
              )
            }
            {isRisMessageEnabled
              && (customRisMessage ? (
                <BuyboxMessage message={customRisMessage} status="info" />
              ) : (
                risMessageEligible
                && !isItemOOS
                && isShowATC
                && !dualPath
                && !virtualAssistance && (
                  <BuyboxMessage message="This item is only returnable in store." status="info" />
                )
              ))}
          </div>
          {isShowATC && (
            <>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <div className="sui-mt-3">
                <BuyboxActions
                  alterBrowserHistory
                  disableATC={isATCDisabled}
                  fulfillment={fulfillment}
                  itemId={itemId}
                  quantity={quantity}
                  storeId={storeId}
                  storeZip={storeZip}
                  channel={channel}
                  zipCode={zipCode}
                  onATCClick={onATCClick}
                  fallbackMode={fallbackMode}
                  subscription={subscription}
                  hideQuantity={!showQuantity}
                  quantityLimit={quantityLimit}
                  paypalEligible={paypalEligible}
                  onQuantityChange={onQuantityChange}
                  configuratorData={configuratorData}
                  availableQuantity={availableQuantity}
                  hidePaypalCheckout={hidePaypalCheckout}
                  fulfillmentMethod={fulfillmentMethod}
                  quantityLimitExceed={quantityLimitExceed}
                  hideInstantCheckout={hideInstantCheckout}
                  minimumOrderQuantity={minimumOrderQuantity}
                  minimumBopisQuantity={minimumBopisQuantity}
                  maximumBopisQuantity={maximumBopisQuantity}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BuyboxLiteQuantity.propTypes = {
  itemId: string,
  unavailable: bool,
  covidAlert: bool,
  bopisHolidayTiming: number,
  storeId: string,
  isShowATC: bool,
  storeZip: string,
  isATCDisabled: bool,
  channel: string,
  zipCode: string,
  storeName: string,
  fulfillmentMethod: string,
  fulfillment: shape({
    backordered: bool
  }),
  onFulfillmentChange: func,
  onDeliveryOptionsZipCodeChange: func,
  onATCClick: func,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any),
  // eslint-disable-next-line react/forbid-prop-types
  configuratorData: arrayOf(PropTypes.any),
  quantityLimit: number,
  currentSelectedFulfillment: string,
  configurableProductDetails: PropTypes.shape({
    isSpecialOrder: bool,
    configuratorHideQuantity: bool,
    /* eslint-disable-next-line react/forbid-prop-types */
    configuratorData: PropTypes.objectOf(PropTypes.any),
    isConfigurableBlinds: bool
  }),
  availableQuantity: shape({
    pickup: number,
    delivery: number
  }),
  subscription: shape({
    discountPercentage: string
  }),
  paypalEligible: bool,
  showQuantity: bool,
  dualPath: bool,
  isItemOOS: bool,
  risMessageEligible: bool,
  isRisMessageEnabled: bool,
  virtualAssistance: bool,
  customRisMessage: string,
  minimumBopisQuantity: number,
  maximumBopisQuantity: number,
  fallbackMode: bool,
  minimumOrderQuantity: number,
  onQuantityChange: func,
  quantity: number,
  isOutOfStockOnline: bool,
  quantityLimitExceed: bool
};

BuyboxLiteQuantity.defaultProps = {
  itemId: null,
  unavailable: false,
  storeId: null,
  isShowATC: true,
  covidAlert: false,
  storeZip: null,
  storeName: null,
  isATCDisabled: false,
  channel: 'desktop',
  zipCode: null,
  fulfillmentMethod: null,
  fulfillment: null,
  paypalEligible: false,
  onFulfillmentChange: /* istanbul ignore next */ () => {},
  onDeliveryOptionsZipCodeChange: /* istanbul ignore next */ () => {},
  onATCClick: /* istanbul ignore next */ () => {},
  bopisHolidayTiming: null,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  directData: null,
  configuratorData: null,
  quantityLimit: 0,
  currentSelectedFulfillment: '',
  configurableProductDetails: {
    isSpecialOrder: false,
    configuratorHideQuantity: false,
    configuratorData: {},
    isConfigurableBlinds: false
  },
  availableQuantity: null,
  subscription: null,
  showQuantity: false,
  minimumBopisQuantity: null,
  maximumBopisQuantity: null,
  fallbackMode: false,
  quantity: 1,
  dualPath: false,
  isItemOOS: false,
  risMessageEligible: false,
  virtualAssistance: false,
  isRisMessageEnabled: false,
  customRisMessage: '',
  onQuantityChange: /* istanbul ignore next */ () => { },
  minimumOrderQuantity: 1,
  quantityLimitExceed: false,
  isOutOfStockOnline: false
};

export { BuyboxLiteQuantity };
